import { notificationService } from './services/notificationService';
import { navigationService } from './services/navigationService';
import { logReminderService } from './services/logReminderService';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { navigation, bateau as bateauAPI, logEntry as logEntryAPI, croisiere as croisiereAPI } from './api';
import LogEntryModal from './components/LogEntryModal';

function Navigation() {
  const { bateauId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [navigations, setNavigations] = useState([]);
  const [activeNavigation, setActiveNavigation] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [showDestinationForm, setShowDestinationForm] = useState(null);
  const [destination, setDestination] = useState('');
  const [destLatitude, setDestLatitude] = useState('');
  const [destLongitude, setDestLongitude] = useState('');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  
  const [showLogEntryModal, setShowLogEntryModal] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [currentCap, setCurrentCap] = useState(null);
  const [bateauData, setBateauData] = useState(null);
  
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    notificationService.requestPermission();
  }, []);

  useEffect(() => {
    logReminderService.onShowLogEntry(() => {
      setShowLogEntryModal(true);
    });
  }, []);

  useEffect(() => {
    loadBateauData();
    loadNavigations();
  }, [bateauId]);

  useEffect(() => {
    // AUTOSTART si paramètre dans l'URL
    const params = new URLSearchParams(location.search);
    const autostart = params.get('autostart');
    
    if (autostart === 'true' && !activeNavigation) {
      handleStartNavigation();
    }
  }, [location.search]);

  const loadBateauData = async () => {
    try {
      const response = await bateauAPI.getAll();
      const bateau = response.data.find(b => b.id === bateauId);
      setBateauData(bateau);
    } catch (err) {
      console.error('Erreur chargement bateau:', err);
    }
  };

  const calculateBearing = (lat1, lon1, lat2, lon2) => {
    const toRad = (deg) => deg * Math.PI / 180;
    const toDeg = (rad) => rad * 180 / Math.PI;

    const dLon = toRad(lon2 - lon1);
    const y = Math.sin(dLon) * Math.cos(toRad(lat2));
    const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
              Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
    
    let bearing = toDeg(Math.atan2(y, x));
    bearing = (bearing + 360) % 360;

    return bearing;
  };

  const startTracking = useCallback((navigationId) => {
    if (!navigator.geolocation) {
      alert('La géolocalisation n\'est pas supportée');
      return;
    }

    let lastPosition = null;

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const newPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          timestamp: new Date()
        };

        if (lastPosition) {
          const cap = calculateBearing(
            lastPosition.lat, lastPosition.lng,
            newPosition.lat, newPosition.lng
          );
          setCurrentCap(cap);
        }

        setCurrentPosition(newPosition);
        lastPosition = newPosition;

        try {
          await navigation.addPosition(navigationId, {
            latitude: newPosition.lat,
            longitude: newPosition.lng,
            timestamp: newPosition.timestamp
          });
          console.log('Position enregistrée:', newPosition);

          if (activeNavigation && activeNavigation.destination) {
            navigationService.checkIfStationary(
              newPosition,
              activeNavigation.destination,
              navigationId
            );
          }
        } catch (err) {
          console.error('Erreur enregistrement position:', err);
        }
      },
      (error) => {
        console.error('Erreur GPS:', error);
        alert('Erreur de géolocalisation: ' + error.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );

    navigationService.startNavigationReminders();
    logReminderService.startReminders(navigationId);

    console.log('Tracking démarré pour navigation:', navigationId);
  }, [activeNavigation]);

  const loadNavigations = useCallback(async () => {
    try {
      const response = await navigation.getByBateau(bateauId);
      setNavigations(response.data);
    } catch (err) {
      console.error(err);
    }
  }, [bateauId]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      navigationService.stopNavigationReminders();
      logReminderService.reset();
    };
  }, []);

  const handleShowDestinationForm = () => {
    if (activeNavigation) {
      const confirmStop = window.confirm('🏁 Êtes-vous sûr de vouloir terminer la navigation du jour ?');
      if (!confirmStop) return;
      
      stopTracking();
      
      // Si position GPS disponible, pré-remplir
      if (currentPosition) {
        setDestLatitude(currentPosition.lat.toFixed(6));
        setDestLongitude(currentPosition.lng.toFixed(6));
      }
      
      // Pré-remplir la destination si elle existe déjà
      if (activeNavigation.destination) {
        setDestination(activeNavigation.destination);
      }
      
      setShowDestinationForm(activeNavigation.id);
    }
  };

  const handleSubmitDestination = async (e) => {
    e.preventDefault();
    
    if (!destLatitude || !destLongitude) {
      alert('Les coordonnées GPS sont requises');
      return;
    }
    
    try {
      // 1. Arrêter la navigation AVEC les coordonnées
      await navigation.stop(
        showDestinationForm,
        destination || activeNavigation.destination || 'Destination',
        parseFloat(destLatitude),
        parseFloat(destLongitude)
      );

      setShowDestinationForm(null);
      setDestination('');
      setDestLatitude('');
      setDestLongitude('');
      
      // 2. Si navigation liée à une croisière, demander si on clôture
      if (activeNavigation.croisiereId) {
        const closeCroisiere = window.confirm('🚢 Voulez-vous clôturer la croisière ?\n\n✅ OUI = Fin de la croisière\n❌ NON = En attente de la prochaine navigation');
        
        if (closeCroisiere) {
          try {
            await croisiereAPI.close(activeNavigation.croisiereId);
            alert('✅ Croisière clôturée avec succès ! Vous allez recevoir le PDF par email.');
          } catch (err) {
            console.error('Erreur clôture croisière:', err);
            alert('Erreur lors de la clôture de la croisière');
          }
        } else {
          alert('📋 Navigation terminée. La croisière reste active pour les prochaines navigations.');
        }
      } else {
        alert('✅ Navigation terminée avec succès !');
      }
      
      setActiveNavigation(null);
      navigationService.reset();
      
      await loadNavigations();
      
      // Retour au dashboard
      navigate('/');
      
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la sauvegarde: ' + (err.response?.data?.message || err.message));
    }
  };

  const stopTracking = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTracking(false);
    navigationService.reset();
    logReminderService.reset();
    console.log('Tracking arrêté');
  };

  const handleStartNavigation = async () => {
    try {
      // Récupérer les paramètres URL
      const params = new URLSearchParams(location.search);
      const croisiereId = params.get('croisiere');
      const destinationParam = params.get('destination') || '';
      
      const response = await navigation.start(bateauId, destinationParam, 0, 0, croisiereId);
      
      const newNav = response.data;
      setActiveNavigation(newNav);
      setIsTracking(true);
      startTracking(newNav.id);
      
      loadNavigations();
      
      // Jouer le son de notification
      const audio = new Audio('/notification-sound.mp3');
      audio.play().catch(err => console.log('Erreur lecture son:', err));
      
      alert('Navigation démarrée !');
    } catch (err) {
      console.error(err);
      alert('Erreur lors du démarrage: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCancelDestination = () => {
    setShowDestinationForm(null);
    setDestination('');
    setDestLatitude('');
    setDestLongitude('');
    
    if (activeNavigation) {
      startTracking(activeNavigation.id);
      setIsTracking(true);
    }
  };

  const handleSubmitLogEntry = async (entry) => {
    try {
      await logEntryAPI.create({
        ...entry,
        navigation_id: activeNavigation.id,
        bateau_id: bateauId
      });

      // Jouer le son de notification
      const audio = new Audio('/notification-sound.mp3');
      audio.play().catch(err => console.log('Erreur lecture son:', err));

      alert('✅ Entrée enregistrée au carnet de bord !');
    } catch (err) {
      console.error('Erreur enregistrement entrée:', err);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  return (
    <div className="navigation-page">
      <h1>🧭 Navigation GPS</h1>

      {activeNavigation && !showDestinationForm && (
        <div className="active-navigation">
          <h2>🟢 Navigation en cours</h2>
          <p>
            <strong>
              {activeNavigation.croisiereId 
                ? `Croisière - Navigation vers ${activeNavigation.destination || 'Non spécifiée'}`
                : `Navigation vers ${activeNavigation.destination || 'Non spécifiée'}`
              }
            </strong>
          </p>
          <p>Démarrée le: {new Date(activeNavigation.startTime).toLocaleString()}</p>
          <p>Status: {isTracking ? '📍 GPS actif' : '⏸️ En pause'}</p>
          
          <div className="nav-actions">
            <button onClick={handleShowDestinationForm} className="btn-danger">
              🏁 Terminer la navigation
            </button>
          </div>
          
          <div className="info-box">
            <p>🔔 Notifications actives :</p>
            <ul>
              <li>Rappel toutes les 5 minutes</li>
              <li>Détection automatique d'arrivée (immobile 2 min)</li>
              <li>📖 Carnet de bord (questionnaire progressif)</li>
            </ul>
          </div>
        </div>
      )}

      {showDestinationForm && (
        <div className="destination-form">
          <h2>📍 Destination finale</h2>
          <form onSubmit={handleSubmitDestination}>
            <div className="form-group">
              <label>Nom de la destination:</label>
              <input
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="Ex: Port de Nieuport"
              />
              <small>Optionnel si déjà renseigné au démarrage</small>
            </div>
            
            <div className="form-group">
              <label>Latitude *:</label>
              <input
                type="number"
                step="any"
                value={destLatitude}
                onChange={(e) => setDestLatitude(e.target.value)}
                placeholder="Ex: 51.1234"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Longitude *:</label>
              <input
                type="number"
                step="any"
                value={destLongitude}
                onChange={(e) => setDestLongitude(e.target.value)}
                placeholder="Ex: 2.5678"
                required
              />
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                ✅ Terminer la navigation
              </button>
              <button type="button" onClick={handleCancelDestination} className="btn-secondary">
                ❌ Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="navigations-history">
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '10px',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '10px'
          }}
          onClick={() => setHistoryExpanded(!historyExpanded)}
        >
          <h2 style={{ margin: 0 }}>📚 Historique des navigations ({navigations.length})</h2>
          <span style={{ fontSize: '1.5rem' }}>{historyExpanded ? '▼' : '▶'}</span>
        </div>
        
        {historyExpanded && (
          <>
            {navigations.length === 0 ? (
              <p>Aucune navigation enregistrée</p>
            ) : (
              <ul>
                {navigations.map(nav => (
                  <li key={nav.id} className={nav.active ? 'active' : ''}>
                    <strong>
                      {nav.croisiereId 
                        ? `Croisière - Navigation vers ${nav.destination || 'Destination inconnue'}`
                        : `Navigation vers ${nav.destination || 'Destination inconnue'}`
                      }
                    </strong>
                    <br />
                    Départ: {new Date(nav.startTime).toLocaleString()}
                    <br />
                    {nav.endTime && `Arrivée: ${new Date(nav.endTime).toLocaleString()}`}
                    <br />
                    Status: {nav.active ? '🟢 En cours' : '✅ Terminée'}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="back-button">
        <button onClick={() => navigate(`/bateau/${bateauId}`)} className="btn-secondary">
          ← Retour au bateau
        </button>
      </div>

      <LogEntryModal
        show={showLogEntryModal}
        onClose={() => setShowLogEntryModal(false)}
        onSubmit={handleSubmitLogEntry}
        bateauType={bateauData?.type || 'voilier'}
        nbMoteurs={bateauData?.nb_moteurs || 1}
        currentPosition={currentPosition}
        currentCap={currentCap}
      />

      <style>{`
        .navigation-page {
          padding: 20px;
          max-width: 800px;
          margin: 0 auto;
        }

        .active-navigation {
          background: #e8f5e9;
          padding: 20px;
          border-radius: 12px;
          margin: 20px 0;
        }

        .info-box {
          background: #fff3cd;
          padding: 15px;
          border-radius: 8px;
          margin-top: 15px;
        }

        .info-box ul {
          margin: 10px 0;
          padding-left: 20px;
        }

        .destination-form {
          background: #fff;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin: 20px 0;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: bold;
        }

        .form-group input {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 16px;
        }

        .form-group small {
          display: block;
          margin-top: 5px;
          color: #666;
          font-size: 0.9rem;
        }

        .nav-actions, .form-actions {
          display: flex;
          gap: 10px;
          margin-top: 15px;
        }

        .btn-primary, .btn-secondary, .btn-danger {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background: #0056b3;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background: #545b62;
        }

        .btn-danger {
          background: #dc3545;
          color: white;
        }

        .btn-danger:hover {
          background: #c82333;
        }

        .navigations-history {
          margin-top: 40px;
        }

        .navigations-history ul {
          list-style: none;
          padding: 0;
        }

        .navigations-history li {
          background: #f8f9fa;
          padding: 15px;
          margin-bottom: 10px;
          border-radius: 8px;
          border-left: 4px solid #6c757d;
        }

        .navigations-history li.active {
          background: #d4edda;
          border-left-color: #28a745;
        }

        .back-button {
          margin-top: 30px;
          text-align: center;
        }
      `}</style>
    </div>
  );
}

export default Navigation;